/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useRef, forwardRef, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import Loader from 'react-loader-spinner';
import {
    Viewer,
    Worker,
    SpecialZoomLevel,
    MinimalButton,
    Tooltip,
    Position,
} from '@react-pdf-viewer/core';
import { toolbarPlugin } from '@react-pdf-viewer/toolbar';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import '@react-pdf-viewer/toolbar/lib/styles/index.css';
import '@react-pdf-viewer/thumbnail/lib/styles/index.css';
import { ZoomInIcon, ZoomOutIcon, zoomPlugin } from '@react-pdf-viewer/zoom';
import { toast } from 'react-toastify';

import { SpinnerBlur } from '../../../components/inbox/Spinner';
import { formatPathName } from '../../../components/commonfunction';
import { unlockDocument, downloadDoc } from '../../api/api';
import print from 'print-js';
import { useImperativeHandle } from 'react';
import { thumbnailPlugin } from '@react-pdf-viewer/thumbnail';

toast.configure();

const PdfPreview = forwardRef(function PdfPreview(props, ref) {
    let isMountedRef = useRef(false);
    const [pdfUrl, setPdfUrl] = useState(null);
    const [apiError, setApiError] = useState(false);
    const [blurSpinner, setBlurSpinner] = useState(false);

    const [selectMode, setSelectMode] = useState(false);
    const [dragStart, setDragStart] = useState(null);
    const [selectionRect, setSelectionRect] = useState(null);

    const [currentPage, setCurrentPage] = useState('');

    const wrapperRef = useRef(null);
    const scaleRef = useRef(SpecialZoomLevel.PageFit);
    const recoverTimerRef = useRef(null);
    const lastScaleRef = useRef(null);
    const isReZoomingRef = useRef(false);
    const inFlightRendersRef = useRef(0);
    const location = useLocation();

    const toolbarPluginInstance = toolbarPlugin();
    const { Toolbar } = toolbarPluginInstance;

    const zoomPluginInstance = zoomPlugin();
    const { zoomTo } = zoomPluginInstance;

    const [showThumbnails, setShowThumbnails] = useState(
        () => localStorage.getItem('pdf-show-thumbnails') !== 'false'
    );

    const syncScalePluginInstance = useMemo(
        () => ({
            onCanvasLayerRender: (e) => {
                if (e.status === 1) {
                    inFlightRendersRef.current = Math.max(
                        0,
                        inFlightRendersRef.current - 1
                    );
                    scaleRef.current = e.scale;
                } else {
                    inFlightRendersRef.current += 1;
                }
            },
        }),
        []
    );

    const thumbnailPluginInstance = thumbnailPlugin();
    const { Thumbnails } = thumbnailPluginInstance;

    const toggleThumbnails = () => {
        setShowThumbnails((value) => {
            const isThumbnailEnabled = !value;
            localStorage.setItem(
                'pdf-show-thumbnails',
                String(isThumbnailEnabled)
            );
            return isThumbnailEnabled;
        });
    };

    useImperativeHandle(
        ref,
        () => {
            return {
                printPdf: () => {
                    props.loader(true);
                    if (pdfUrl == null) {
                        let documentPromise;
                        if (props.docId !== undefined) {
                            const docId = props.docId;
                            documentPromise = downloadDoc(docId);
                            if (documentPromise !== undefined) {
                                documentPromise
                                    .then((response) => {
                                        if (response.status === 400) {
                                            props.loader(false);
                                            return new Error('error');
                                        }
                                        if (response.status === 204) {
                                        }
                                        return response.blob();
                                    })
                                    .then((data) => {
                                        const url = URL.createObjectURL(data);
                                        print({
                                            onError: (e) => {
                                                console.log(e);
                                            },
                                            type: 'pdf',
                                            printable: url,
                                            onLoadingEnd: () => {
                                                props.loader(false);
                                            },
                                            onPrintDialogClose: () => {
                                                props.loader(false);
                                            },
                                        });
                                    })
                                    .catch((error) => {});
                            } else {
                                props.loader(false);
                            }
                        } else {
                            props.loader(false);
                        }
                    } else {
                        print({
                            onError: (e) => {
                                console.log(e);
                            },
                            type: 'pdf',
                            printable: pdfUrl,
                            onLoadingEnd: () => {
                                props.loader(false);
                            },
                            onPrintDialogClose: () => {
                                props.loader(false);
                            },
                        });
                    }
                },
            };
        },
        [pdfUrl, props.loader]
    );

    useEffect(() => {
        isMountedRef.current = true;
        setPdfUrl(null);
        setSelectMode(false);
        let documentPromise;
        let pathname = location.pathname;
        const currentPage = pathname.split('/');
        if (currentPage.length > 0 && isMountedRef.current) {
            if (currentPage[2] === 'detail') {
                setCurrentPage(currentPage[2]);
            }
        }
        if (props.docId !== undefined && isMountedRef.current) {
            pathname = formatPathName(pathname);
            localStorage.setItem(`locked-document-id-${pathname}`, props.docId);
            const docId = props.docId;
            documentPromise = downloadDoc(docId);
        }
        if (props.docId === undefined && isMountedRef.current) {
            const pathname = location.pathname.split('/');

            if (pathname[1] === 'detail') {
                documentPromise = downloadDoc(pathname[2]);
            }
        }

        if (documentPromise !== undefined) {
            documentPromise
                .then(async (response) => {
                    const status = response.status;
                    const data = await response.blob();

                    if (status === 400 && data.size === 0) {
                        if (isMountedRef.current) {
                            setApiError(true);
                            toast.error('No pdf to load', {
                                position: 'top-right',
                                autoClose: 3000,
                                hideProgressBar: false,
                                closeOnClick: true,
                                pauseOnHover: true,
                                draggable: true,
                                progress: undefined,
                                showTransitionSuccess: false,
                            });
                            if (props.updateLocksList) {
                                props.updateLocksList();
                            }
                        }
                        return null;
                    }

                    if (status === 204) {
                        if (isMountedRef.current) {
                            setApiError(false);
                        }
                        return null;
                    }

                    return data;
                })
                .then((data) => {
                    if (!data) {
                        return;
                    }
                    if (isMountedRef.current) {
                        const url = URL.createObjectURL(data);
                        setPdfUrl(url);
                        setBlurSpinner(false);
                        if (props.updateLocksList) {
                            props.updateLocksList();
                        }
                    }
                })
                .catch((error) => {});
        }

        return () => {
            if (props.docId !== undefined) {
                const docId = props.docId;
                unlockDocument(docId).then((data) => {
                    if (props.removeLockFromList && !props.isLocked) {
                        props.removeLockFromList(docId);
                    }
                });
            }
            isMountedRef.current = false;
        };
    }, [props.docId]);

    var pdfHeight = window.innerHeight;

    if (props.isLocked === true) {
        pdfHeight = window.innerHeight - 370;
    }

    const onMouseDown = (e) => {
        if (!selectMode || !wrapperRef.current) return;
        const rect = wrapperRef.current.getBoundingClientRect();
        setDragStart({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    };

    const onMouseMove = (e) => {
        if (!selectMode || !dragStart || !wrapperRef.current) return;
        const rect = wrapperRef.current.getBoundingClientRect();
        const x2 = e.clientX - rect.left;
        const y2 = e.clientY - rect.top;
        setSelectionRect({
            x: Math.min(dragStart.x, x2),
            y: Math.min(dragStart.y, y2),
            w: Math.abs(dragStart.x - x2),
            h: Math.abs(dragStart.y - y2),
        });
    };

    const onMouseUp = () => {
        if (!selectMode || !selectionRect || !wrapperRef.current) {
            setDragStart(null);
            return;
        }

        const pages = wrapperRef.current.querySelector(
            '[data-testid="core__inner-pages"]'
        );
        const canvas = pages?.querySelector('.rpv-core__canvas-layer canvas');

        if (!pages || !canvas) {
            setDragStart(null);
            setSelectionRect(null);
            setSelectMode(false);
            return;
        }

        const canvasRect = canvas.getBoundingClientRect();
        const wrapperRect = wrapperRef.current.getBoundingClientRect();

        const relativeSelection = {
            x: selectionRect.x - (canvasRect.left - wrapperRect.left),
            y: selectionRect.y - (canvasRect.top - wrapperRect.top),
            w: selectionRect.w,
            h: selectionRect.h,
        };

        const oldScale = scaleRef.current;

        if (relativeSelection.w < 10 || relativeSelection.h < 10) {
            setDragStart(null);
            setSelectionRect(null);
            setSelectMode(false);
            return;
        }

        const newScaleX = pages.clientWidth / relativeSelection.w;
        const newScaleY = pages.clientHeight / relativeSelection.h;
        const newScale = Math.min(newScaleX, newScaleY) * oldScale;

        const clampedScale = Math.min(newScale, 15);

        zoomTo(clampedScale);

        setTimeout(() => {
            const scaleRatio = clampedScale / oldScale;

            const newCenterX =
                (relativeSelection.x + relativeSelection.w / 2) * scaleRatio;
            const newCenterY =
                (relativeSelection.y + relativeSelection.h / 2) * scaleRatio;

            pages.scrollLeft = newCenterX - pages.clientWidth / 2;
            pages.scrollTop = newCenterY - pages.clientHeight / 2;
            window.dispatchEvent(new Event('resize'));
        }, 100);

        setDragStart(null);
        setSelectionRect(null);
        setSelectMode(false);
    };

    const onSelectAreaClick = () => {
        setSelectMode((v) => !v);
    };

    const hasBlankVisibleCanvas = () => {
        const container = wrapperRef.current;
        if (!container) return false;
        const viewTop = 0;
        const viewBottom = container.clientHeight || window.innerHeight;
        const canvases = container.querySelectorAll(
            '.rpv-core__canvas-layer canvas'
        );
        const containerTop = container.getBoundingClientRect().top;

        for (const canvas of canvases) {
            if (!canvas.width || !canvas.height) continue;
            const rect = canvas.getBoundingClientRect();
            const top = rect.top - containerTop;
            if (
                rect.height === 0 ||
                top > viewBottom ||
                top + rect.height < viewTop
            )
                continue;
            try {
                const ctx = canvas.getContext('2d');
                const px = ctx.getImageData(
                    canvas.width >> 1,
                    canvas.height >> 1,
                    1,
                    1
                ).data;
                if (px[3] === 0) return true;
            } catch (err) {}
        }
        return false;
    };

    const scheduleBlackScreenRecovery = () => {
        if (recoverTimerRef.current) {
            clearTimeout(recoverTimerRef.current);
        }
        recoverTimerRef.current = setTimeout(function attempt() {
            if (inFlightRendersRef.current > 0) {
                recoverTimerRef.current = setTimeout(attempt, 120);
                return;
            }
            const scale = lastScaleRef.current;
            if (typeof scale !== 'number' || !isFinite(scale)) return;
            if (!hasBlankVisibleCanvas()) return;
            isReZoomingRef.current = true;
            zoomTo(scale * 1.0001);
        }, 250);
    };

    const handleZoom = (e) => {
        if (isReZoomingRef.current) {
            isReZoomingRef.current = false;
            return;
        }
        if (e && typeof e.scale === 'number') {
            lastScaleRef.current = e.scale;
        }
        scheduleBlackScreenRecovery();
    };

    useEffect(() => {
        return () => {
            if (recoverTimerRef.current) {
                clearTimeout(recoverTimerRef.current);
            }
        };
    }, []);

    const viewerPlugins = useMemo(
        () => [
            toolbarPluginInstance,
            zoomPluginInstance,
            syncScalePluginInstance,
            thumbnailPluginInstance,
        ],
        [
            toolbarPluginInstance,
            zoomPluginInstance,
            syncScalePluginInstance,
            thumbnailPluginInstance,
        ]
    );

    return (
        <>
            {pdfUrl ? (
                <div
                    className='rpv-core__viewer'
                    style={{
                        display: 'flex',
                        height: '100%',
                    }}
                >
                    <div className='toolbar-pdfpreview'>
                        <div className='pdfpreview-tools'>
                            <Toolbar>
                                {(props) => {
                                    const {
                                        CurrentPageInput,
                                        EnterFullScreen,
                                        GoToNextPage,
                                        GoToPreviousPage,
                                        NumberOfPages,
                                        ShowSearchPopover,
                                        Zoom,
                                        ZoomIn,
                                        ZoomOut,
                                    } = props;

                                    return (
                                        <>
                                            <div>
                                                <ShowSearchPopover />
                                            </div>
                                            <div>
                                                <GoToPreviousPage />
                                            </div>
                                            <div>
                                                <CurrentPageInput />/
                                                <NumberOfPages />
                                            </div>
                                            <div>
                                                <GoToNextPage />
                                            </div>
                                            <div style={{ marginLeft: 'auto' }}>
                                                <ZoomOut>
                                                    {({
                                                        onClick,
                                                        isDisabled,
                                                        enableShortcuts,
                                                    }) => (
                                                        <Tooltip
                                                            position={
                                                                Position.BottomCenter
                                                            }
                                                            target={
                                                                <MinimalButton
                                                                    ariaLabel='Zoom out'
                                                                    ariaKeyShortcuts={
                                                                        enableShortcuts
                                                                            ? 'Ctrl+-'
                                                                            : undefined
                                                                    }
                                                                    isDisabled={
                                                                        isDisabled
                                                                    }
                                                                    onClick={(
                                                                        e
                                                                    ) => {
                                                                        if (
                                                                            isDisabled
                                                                        ) {
                                                                            return;
                                                                        }
                                                                        onClick(
                                                                            e
                                                                        );
                                                                    }}
                                                                >
                                                                    <ZoomOutIcon />
                                                                </MinimalButton>
                                                            }
                                                            content={() =>
                                                                'Zoom out'
                                                            }
                                                            offset={{
                                                                left: 0,
                                                                top: 8,
                                                            }}
                                                        />
                                                    )}
                                                </ZoomOut>
                                            </div>
                                            <div>
                                                <Zoom
                                                    levels={[0.4, 0.8, 1.2]}
                                                />
                                            </div>
                                            <div>
                                                <ZoomIn>
                                                    {({
                                                        onClick,
                                                        isDisabled,
                                                        enableShortcuts,
                                                    }) => (
                                                        <Tooltip
                                                            position={
                                                                Position.BottomCenter
                                                            }
                                                            target={
                                                                <MinimalButton
                                                                    ariaLabel='Zoom in'
                                                                    ariaKeyShortcuts={
                                                                        enableShortcuts
                                                                            ? 'Ctrl+='
                                                                            : undefined
                                                                    }
                                                                    isDisabled={
                                                                        isDisabled
                                                                    }
                                                                    onClick={(
                                                                        e
                                                                    ) => {
                                                                        if (
                                                                            isDisabled
                                                                        ) {
                                                                            return;
                                                                        }
                                                                        onClick(
                                                                            e
                                                                        );
                                                                    }}
                                                                >
                                                                    <ZoomInIcon />
                                                                </MinimalButton>
                                                            }
                                                            content={() =>
                                                                'Zoom in'
                                                            }
                                                            offset={{
                                                                left: 0,
                                                                top: 8,
                                                            }}
                                                        />
                                                    )}
                                                </ZoomIn>
                                            </div>
                                            <div
                                                title='Select area to zoom'
                                                className={`select-area-btn ${
                                                    selectMode ? 'active' : ''
                                                }`}
                                                style={{ marginLeft: 8 }}
                                                onClick={onSelectAreaClick}
                                            >
                                                <i className='fa-solid fa-object-group'></i>
                                            </div>
                                            <div style={{ marginLeft: 'auto' }}>
                                                <div className='d-flex'>
                                                    <div
                                                        title={
                                                            showThumbnails
                                                                ? 'Hide thumbnails'
                                                                : 'Show thumbnails'
                                                        }
                                                        className={`toggle-thumbs-btn`}
                                                        style={{
                                                            marginLeft: 'auto',
                                                        }}
                                                        onClick={
                                                            toggleThumbnails
                                                        }
                                                    >
                                                        <i className='fa-regular fa-rectangle-list' />
                                                    </div>
                                                    <EnterFullScreen />
                                                </div>
                                            </div>
                                        </>
                                    );
                                }}
                            </Toolbar>
                        </div>

                        <div
                            ref={wrapperRef}
                            className='pdf-scroll'
                            id='pdf-scroll'
                            style={{
                                flex: 1,
                                height: pdfHeight,
                                position: 'relative',
                                cursor: selectMode ? 'crosshair' : 'auto',
                            }}
                            onMouseDown={onMouseDown}
                            onMouseMove={onMouseMove}
                            onMouseUp={onMouseUp}
                        >
                            {selectMode && selectionRect && (
                                <div
                                    style={{
                                        position: 'absolute',
                                        left: selectionRect.x,
                                        top: selectionRect.y,
                                        width: selectionRect.w,
                                        height: selectionRect.h,
                                        pointerEvents: 'none',
                                        border: '2px solid #00c8ff',
                                        background: 'rgba(0,200,255,.15)',
                                        borderRadius: 2,
                                        zIndex: 20,
                                    }}
                                />
                            )}
                            <Worker workerUrl='https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js'>
                                <Viewer
                                    fileUrl={pdfUrl}
                                    plugins={viewerPlugins}
                                    defaultScale={SpecialZoomLevel.PageFit}
                                    onZoom={handleZoom}
                                    onDocumentLoad={() => {
                                        requestAnimationFrame(() => {
                                            requestAnimationFrame(() => {
                                                window.dispatchEvent(
                                                    new Event('resize')
                                                );
                                            });
                                        });
                                    }}
                                />
                            </Worker>
                        </div>
                    </div>

                    {showThumbnails && (
                        <aside
                            className='pdf-thumbs'
                            style={{
                                width: 170,
                                height: pdfHeight,
                                borderLeft: '1px solid #eee',
                                padding: 8,
                            }}
                        >
                            <Thumbnails />
                        </aside>
                    )}
                </div>
            ) : apiError === false ? (
                <div
                    id={currentPage === 'detail' ? 'right-aside' : 'left-aside'}
                    className={
                        currentPage === 'detail'
                            ? 'left-size-loader'
                            : 'right-size-loader bbb'
                    }
                    style={
                        currentPage === 'detail' ? { textAlign: 'center' } : {}
                    }
                >
                    Loading .....
                    <Loader
                        type='Oval'
                        color='#007dbc'
                        height={40}
                        width={40}
                    />
                </div>
            ) : null}
            <SpinnerBlur show={blurSpinner} />
        </>
    );
});

export default PdfPreview;
